// Service de calcul automatique des devis selon le type de projet

/**
 * Calcule la quantité nécessaire pour un produit selon son type et le type de projet
 * @param {string} productType - Type du produit (LAMBOURDE, BANDE_BITUMINEUSE, etc.)
 * @param {string} projectType - Type de projet (TERASSE_EXTERIEUR, BARDAGE, SOL, MUR_PLAQUE_PLATRE)
 * @param {number} surfaceArea - Surface en m²
 * @param {Object} userInputs - Inputs utilisateur par section
 * @returns {number} Quantité calculée
 */
export const calculateProductQuantity = (productType, projectType, surfaceArea, userInputs = {}) => {
  // Récupérer la surface depuis les inputs utilisateur ou utiliser la surface par défaut
  const terraceArea = getTerraceArea(userInputs, projectType) || surfaceArea;
  
  switch (projectType) {
    case 'TERASSE_EXTERIEUR':
      return calculateTerraceQuantity(productType, terraceArea);
    case 'BARDAGE':
      return calculateBardageQuantity(productType, terraceArea);
    case 'SOL':
      return calculateSolQuantity(productType, terraceArea);
    case 'MUR_PLAQUE_PLATRE':
      return calculateMurPlaquePlatreQuantity(productType, terraceArea);
    default:
      return 1; // Quantité par défaut
  }
};

/**
 * Récupère la surface depuis les inputs utilisateur
 * Pour les terrasses, on cherche dans la section "TAILLE DE VOTRE TERRASSE"
 */
const getTerraceArea = (userInputs, projectType) => {
  console.log('Recherche de la surface dans userInputs:', userInputs);
  
  // Pour tous les types de projets, chercher la surface dans les inputs
  for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
    console.log('Vérification de la section:', sectionId, 'avec inputs:', sectionInputs);
    
    // Chercher dans les inputs de la section
    for (const [key, inputData] of Object.entries(sectionInputs)) {
      console.log('Vérification de l\'input:', key, 'avec data:', inputData);
      
      // Extraire la valeur selon le format
      let value = inputData;
      if (typeof inputData === 'object' && inputData !== null && inputData.value !== undefined) {
        value = inputData.value;
      }
      
      // Vérifier si c'est l'input "Taille de votre terrasse" (par clé ou par label)
      const isTerraceSizeInput = key.toLowerCase().includes('taille') || 
                                key.toLowerCase().includes('terrasse') ||
                                (typeof inputData === 'object' && inputData.label && 
                                 (inputData.label.toLowerCase().includes('taille') || 
                                  inputData.label.toLowerCase().includes('terrasse')));
      
      if (isTerraceSizeInput) {
        const numericValue = parseFloat(value);
        console.log('Taille de terrasse trouvée:', numericValue, 'dans la section:', sectionId);
        if (!isNaN(numericValue) && numericValue > 0) {
          return numericValue;
        }
      }
    }
  }
  
  console.log('Aucune taille de terrasse trouvée');
  return null;
};

/**
 * Calculs pour les projets de type TERASSE_EXTERIEUR
 * Utilise la surface renseignée dans la section "TAILLE DE VOTRE TERRASSE" si disponible
 */
const calculateTerraceQuantity = (productType, surfaceArea) => {
  // Utiliser la surface renseignée ou une surface par défaut de 1 m²
  const area = surfaceArea || 1;
  console.log('Calcul terrasse pour', productType, 'avec surface:', area);
  
  switch (productType) {
    case 'LAMBOURDE':
      // ML total = m² terrasse x 2,5
      return Math.ceil(area * 2.5);
      
    case 'BANDE_BITUMINEUSE':
      // UN total = total ML de lambourde / 20 avec arrondi supérieur
      const lambourdeML = area * 2.5;
      return Math.ceil(lambourdeML / 20);
      
    case 'CALE_VENTILATION':
      // UN total = m² terrasse / 19 avec arrondi supérieur
      return Math.ceil(area / 19);
      
    case 'VISERIE':
      // BT total = m² terrasse x 40 / 400 avec arrondi supérieur
      return Math.ceil((area * 40) / 400);
      
    case 'PLOTS_DAKOTA':
      // UN total = m² terrasse x 4
      return Math.ceil(area * 4);
      
    case 'CALE_D"ISOLATION':
      // BT total = m² terrasse x 4 / 25 avec arrondi supérieur
      return Math.ceil((area * 4) / 25);
      
    case 'GEOTEXTILE':
      // UN total = m² terrasse / 25 avec arrondi supérieur
      return Math.ceil(area / 25);
      
    case 'FORET_POUR_PRE_PREÇAGE':
      // UN total = m² terrasse / 70 avec arrondi supérieur
      return Math.ceil(area / 70);
      
    case 'TRAITS_DÉCOUP':
      // 1 UN
      return 1;
      
    case 'ESPACEUR_&_KIT_SERRE_LAME':
      // 1 UN
      return 1;
      
    default:
      return 1;
  }
};

/**
 * Calculs pour les projets de type BARDAGE
 */
const calculateBardageQuantity = (productType, surfaceArea) => {
  const area = surfaceArea || 1;
  
  switch (productType) {
    case 'LAMBOURDE':
      // Formule spécifique au bardage
      return Math.ceil(area * 1.5);
      
    case 'VISERIE':
      // Formule spécifique au bardage
      return Math.ceil(area * 20);
      
    default:
      return 1;
  }
};

/**
 * Calculs pour les projets de type SOL
 */
const calculateSolQuantity = (productType, surfaceArea) => {
  const area = surfaceArea || 1;
  
  switch (productType) {
    case 'LAMBOURDE':
      // Formule spécifique au sol
      return Math.ceil(area * 2);
      
    case 'VISERIE':
      // Formule spécifique au sol
      return Math.ceil(area * 30);
      
    default:
      return 1;
  }
};

/**
 * Calculs pour les projets de type MUR_PLAQUE_PLATRE
 */
const calculateMurPlaquePlatreQuantity = (productType, surfaceArea) => {
  const area = surfaceArea || 1;
  
  switch (productType) {
    case 'LAMBOURDE':
      // Formule spécifique au mur plaque plâtre
      return Math.ceil(area * 1.2);
      
    case 'VISERIE':
      // Formule spécifique au mur plaque plâtre
      return Math.ceil(area * 15);
      
    default:
      return 1;
  }
};

/**
 * Calcule le prix total pour un produit avec sa quantité calculée
 * @param {Object} product - Produit avec price
 * @param {string} productType - Type du produit
 * @param {string} projectType - Type de projet
 * @param {number} surfaceArea - Surface en m²
 * @param {Object} userInputs - Inputs utilisateur
 * @returns {Object} { quantity, totalPrice }
 */
export const calculateProductPricing = (product, productType, projectType, surfaceArea, userInputs) => {
  const calculatedQuantity = calculateProductQuantity(productType, projectType, surfaceArea, userInputs);
  const unitPrice = parseFloat(product.price) || 0;
  const totalPrice = calculatedQuantity * unitPrice;
  
  return {
    quantity: calculatedQuantity,
    totalPrice: totalPrice
  };
};

/**
 * Applique le taux de chute pour les lames de terrasse (+7%)
 * @param {number} baseQuantity - Quantité de base
 * @param {string} productType - Type du produit
 * @returns {number} Quantité avec taux de chute
 */
export const applyWasteRate = (baseQuantity, productType) => {
  // Appliquer le taux de chute uniquement pour les lames de terrasse
  if (productType === 'LAMES_TERRASSE' || productType.toLowerCase().includes('lame')) {
    return Math.ceil(baseQuantity * 1.07); // +7%
  }
  return baseQuantity;
};
